import { useState } from 'react';

import { useApiClient, useSearchPageState } from '@klareospace/pharos-lib-front-utils';
import type { UseSearchPageStateReturn } from '@klareospace/pharos-lib-front-utils';
import type { Carrier, CarrierType, SortDirection } from '@klareospace/pharos-lib-types';
import { useQuery } from '@tanstack/react-query';

import type { SearchCarriersParams } from '../api';
import { createSiteApi } from '../api';
import type { CarrierSortableColumn } from '../types/carrier.types';
import { toValidCarrierTypes } from '../utils';
import { useCarriers } from './use-carriers';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CarrierWithSiteCount extends Carrier {
  enabledSitesCount: number;
}

export interface UseCarriersPageOptions {
  spaceId: string;
}

export interface UseCarriersPageReturn {
  carriers: CarrierWithSiteCount[];
  currentPage: number;
  error: Error | null;
  isLoading: boolean;
  onPageChange: (newPage: number) => void;
  onSearchChange: (value: string) => void;
  onSort: (sortBy: CarrierSortableColumn, sortDirection: SortDirection | null) => void;
  onTypeFilterChange: (values: string[]) => void;
  pageSize: number;
  searchValue: string;
  sortBy: CarrierSortableColumn | undefined;
  sortDirection: SortDirection | undefined;
  totalItems: number;
  totalPages: number;
  totalSitesInSpace: number;
  typeFilter: CarrierType[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toSpaceCarriersParams(
  searchState: UseSearchPageStateReturn<CarrierSortableColumn>,
  spaceId: string,
  typeFilter: CarrierType[],
): SearchCarriersParams {
  return {
    page: searchState.currentPage,
    pageSize: searchState.pageSize,
    search: searchState.debouncedSearch || undefined,
    sort: searchState.sort,
    spaceId,
    types: typeFilter.length > 0 ? typeFilter : undefined,
  };
}

function computeEnabledSitesCount(carrier: Carrier, spaceId: string, totalSitesInSpace: number): number {
  const scope = carrier.enabledScopes.find((s) => s.spaceId === spaceId);
  if (!scope) return 0;
  if (scope.enabledOnAllSites) {
    const disabledCount = scope.siteSpecifics?.filter((s) => s.disabled).length ?? 0;
    return totalSitesInSpace - disabledCount;
  }
  return scope.siteSpecifics?.filter((s) => !s.disabled).length ?? 0;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCarriersPage({ spaceId }: UseCarriersPageOptions): UseCarriersPageReturn {
  const searchState = useSearchPageState<CarrierSortableColumn>();
  const [typeFilter, setTypeFilter] = useState<CarrierType[]>([]);
  const client = useApiClient('admin-auth');
  const siteApi = createSiteApi(client);

  const { data, error, isLoading } = useCarriers(toSpaceCarriersParams(searchState, spaceId, typeFilter));

  const { data: siteCountData } = useQuery({
    queryFn: () => siteApi.countBySpace([spaceId]),
    queryKey: ['sites', 'count-by-space', spaceId],
  });

  const totalSitesInSpace = siteCountData?.[spaceId] ?? 0;

  const carriers: CarrierWithSiteCount[] = (data?.items ?? []).map((carrier) => ({
    ...carrier,
    enabledSitesCount: computeEnabledSitesCount(carrier, spaceId, totalSitesInSpace),
  }));

  const onTypeFilterChange = (values: string[]): void => {
    setTypeFilter(toValidCarrierTypes(values));
    searchState.onPageChange(1);
  };

  return {
    carriers,
    currentPage: searchState.currentPage,
    error: error ?? null,
    isLoading,
    onPageChange: searchState.onPageChange,
    onSearchChange: searchState.onSearchChange,
    onSort: searchState.onSort,
    onTypeFilterChange,
    pageSize: searchState.pageSize,
    searchValue: searchState.searchValue,
    sortBy: searchState.sortBy,
    sortDirection: searchState.sortDirection,
    totalItems: data?.total ?? 0,
    totalPages: data?.totalPages ?? 1,
    totalSitesInSpace,
    typeFilter,
  };
}
